// Das Ufer (§6): gemeinsamer Zustand und kleine Hilfen für Aufstellen, Seitenleiste und Abspielen.
// Aufgeteilt aus shore.ts (CLAUDE.md: Dateien über etwa 1500 Zeilen aufteilen).
import {
  combatRules,
  getClass,
  friendStats,
  getSpecies,
  lineups,
  species as allSpecies,
  traitHidden,
  systemOn,
} from '../../content/index.ts';
import {
  rollTraits,
  type BattleResult,
  type CombatProfile,
  type EnemyPlacement,
  type Unit,
} from '../../sim/index.ts';
import { type Cursor, type DrawUnit } from '../render.ts';
import { enemyNames } from '../summary.ts';

export const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
export const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

export const FIELD = combatRules.field;

export const FZ = FIELD.friendZone;

export const SPEEDS = [0.5, 1, 2, 4];

/** 1× ist etwas langsamer als Echtzeit, damit man folgen kann (Fabi, Phase 3) */
/** Fabi (Phase Kern, 17.10.2026 nachts): 1× war gefühlt zu schnell; 1× bleibt 1×, läuft aber 15 % langsamer als zuvor [ZAHL?] */
export const BASE_SPEED = 0.77;

export const SHORT: Record<string, string> = {
  dachs: 'Da',
  igel: 'Ig',
  eichhoernchen: 'Eh',
  eisvogel: 'Ev',
  wasserspitzmaus: 'Sm',
  waschbaer: 'Wb',
  elster: 'El',
  feuersalamander: 'Fs',
  schlammling: 'Sl',
  treibholzer: 'Th',
  windschnitt: 'Wi',
  hagelchen: 'Ha',
  dreckschleuder: 'Dr',
  blitzflitzer: 'Bf',
  donnergroller: 'Dg',
  prallgraupel: 'Pg',
  nieselchen: 'Ni',
};

export const short = (id: string, name: string) => SHORT[id] ?? name.slice(0, 2);

/** G18, Runde 7: wie sich ein Sturmwesen verhält, in einem Satz */
export const BEHAVIOR_TEXT: Record<string, string> = {
  nearest: 'Geht auf das nächste Tier.',
  hindmost: 'Geht auf das hinterste Tier.',
  raider: 'Springt kurz in die hintere Reihe, greift ein paarmal an und springt zurück.',
  // Fabi (17.10.2026): „In ihrem Text steht ausdrücklich, sie würde das nicht tun“; allein wehrt sie sich, das steht jetzt dabei
  support: 'Greift nicht an, sondern bewirft die schwächsten Sturmwesen mit Dreck, der sie schützt. Ist sie als Letzte übrig, wehrt sie sich selbst.',
  flitter: 'Huscht zu einem zufälligen Tier, schlägt ein- bis viermal zu und huscht weiter.',
  crowd: 'Zieht langsam und unaufhörlich dorthin, wo die meisten Tiere stehen.',
  bounce: 'Wirft sich gegen sein Ziel und landet wieder auf Abstand; so hält er Nahkämpfer hin.',
  // Fabi (17.10.2026): „zweite Reihe“ war eine Erklärung für Claude, kein Spieltext
  skirmisher: 'Springt zu einem angeschlagenen Tier gleich hinter den vordersten, schlägt ein paarmal zu und springt zurück.',
  hider: 'Versteckt sich hinter einem stärkeren Sturmwesen und wirft von dort Blätter. Ist keines mehr da, hinter dem es sich verstecken kann, kämpft es mit dem Blattschwert aus der Nähe.',
};

export const fmt = (x: number, d = 1) => x.toFixed(d).replace('.', ',');

export const stormName = (id: string) => combatRules.stormLevels.find((l) => l.id === id)!.name;

export const ALL_FRIENDS = allSpecies.map((s) => s.id);

/** Wer aufs Ufer kann. Im Run kommen Werte, Leben und Items aus dem Kampfprofil (R11, F7, I1). */
/** path (Fabi, Phase Kern): der Weg des Freundes, Ast und Stufen mit ihrer Wirkung, damit man beim Aufstellen weiß, was er kann */
export type Member = {
  key: string;
  species: string;
  friendClass: string;
  name: string;
  profile?: CombatProfile;
  level?: number;
  items?: string[];
  path?: { level: number; name: string; text: string; branch: boolean }[];
  /** F42 (Phase Kern): verwundet; fällt er noch einmal, nimmt er Abschied */
  wounded?: boolean;
  /** S10 Leibwache: der zuletzt gewählte Schützling */
  ward?: string | null;
  /** T37: Aussehen innerhalb der Spezies (0 wie gezeichnet) */
  look?: number;
};

// Phase 3 (Fabi): In der Kampf-Übung stehen von jeder Spezies welche bereit. Phase Kern (Fabi): jede Klasse, je zwei
export const PRACTICE: Member[] = ALL_FRIENDS.flatMap((sp) =>
  getSpecies(sp).classes.flatMap((c) =>
    [1, 2].map((n) => ({ key: `${sp}-${c.friendClass}-${n}`, species: sp, friendClass: c.friendClass, name: `${getSpecies(sp).name} ${getClass(c.friendClass).name} ${n}` })),
  ),
).map((m, i, all) => ({ ...m, look: all.slice(0, i).filter((o) => o.species === m.species).length })); // T37: jede Übungsfigur ihrer Spezies sieht anders aus

export const member = (key: string): Member => S.roster.find((m) => m.key === key)!;

/** F2, Q62: Werte aus dem Kampfprofil oder der Klasse, die Größe immer von der Spezies */
export const statsOf = (m: Member) => ({ ...(m.profile?.stats ?? friendStats(m.species, m.friendClass)), radius: getSpecies(m.species).radius });

export const hpOf = (m: Member) => m.profile?.hp ?? statsOf(m).hp;

/** K4: In der Übung fest sechs Feldplätze; der XP-Topf richtet sich danach, nicht nach den Aufgestellten (F15) */
export const FIELD_SLOTS = 6;

/** Kampf im Run: Wer, gegen wen, und was danach passiert */
export type RunShoreContext = {
  title: string;
  roster: Member[];
  fieldSlots: number;
  enemies: EnemyPlacement[];
  storm: string;
  retreat: string;
  /** Führt den Kampf in der Run-Simulation aus und gibt ihn mit Bildern zum Abspielen zurück */
  fight: (placements: { id: string; x: number; y: number; march?: { x: number; y: number }; ward?: string }[], retreat: string) => BattleResult;
  /** Zeilen für die Übersicht nach dem Kampf: XP, Level-Ups, Beute */
  afterNotes: () => string;
  onDone: () => void;
  /** Vorschlag zum Aufstellen */
  suggest: (ids: string[]) => { id: string; x: number; y: number }[];
  /** K17: am Durchlass kein Rückzug */
  noRetreat?: boolean;
  /** I26: welche Rückzugsschwellen es im Run gibt (ängstlich nur mit Hasenfuß) */
  retreatOptions?: string[];
  /** P4 (Fabi, Runde 8): Was diesen Kampf verändert, etwa ein angelocktes Sturmwesen */
  notes?: string[];
  /** Nachdem die Übersicht im Bild ist (z. B. XP-Balken füllen) */
  summaryShown?: (root: HTMLElement) => void;
  /** F22, Q43: wer statt Abschied wie versprochen nachkommt */
  kept?: () => string[];
  /** XP, die ein Freund im Run wirklich bekommt (ohne Item-Bonus) */
  xpOf?: (id: string | null) => number | undefined;
  /** R41 (Erste Fassung): Biom des Tages, für Boden, Licht und Laub */
  biome?: string;
  /** R42: Kampf am Durchlass; der Durchlass steht dann am rechten Rand */
  boss?: boolean;
  /** B25: Wer aus dem Gemeinschaftsraum verbunden ist (Kennungen), und wie nah sie stehen müssen */
  bonds?: { groups: string[][]; reach: number };
};

export type Screen = 'setup' | 'battle' | 'summary';

export type Held = { key: string; from: 'field' | 'bench'; x: number; y: number; byMouse: boolean };

export const S = {
  mode: 'practice' as 'practice' | 'run',
  run: null as RunShoreContext | null,
  roster: PRACTICE,
  screen: 'setup' as Screen,
  lineupIdx: 0,
  storm: 'ruhig',
  fieldSlots: FIELD_SLOTS,
  retreat: systemOn('retreat') ? combatRules.retreatThresholds.find((r) => r.default)!.id : 'letzter',
  attempt: 0,
  enemies: [] as EnemyPlacement[],
  /** Freund (Schlüssel aus ROSTER) → Platz; Einfügereihenfolge = Reihenfolge auf dem Feld */
  placed: new Map<string, { x: number; y: number }>(),
  held: null as Held | null,
  cursor: { x: 38, y: 50, holding: null, visible: false } as Cursor,
  hoverUid: null as number | null,
  /** Im Kampf angeklickt: Seitenleiste zeigt den Verlauf dieser Figur (L7) */
  selectedUid: null as number | null,
  message: '',
  result: null as BattleResult | null,
  crits: new Map<number, number>(),
  playT: 0,
  /** T31 (Fabi, 17.10.2026 nachts): nach dem Kampf laufen die Bewegungen noch zu Ende, statt einzufrieren */
  afterT: 0,
  speed: 1,
  paused: false,
  /** Nach dem Kampf: Zwischenbildschirm „Weiter“ oder „Ufer betrachten“ */
  endOverlay: false,
  /** Ufer betrachten: Zeitleiste frei, Gegangene als Umriss anklickbar */
  reviewing: false,
  /** Weiter als bis hierhin darf man nicht spulen, solange der Kampf läuft: kein Blick in die Zukunft (Fabi, Phase 3) */
  maxSeenT: 0,
  /** Kampf einmal bis zum Ende gesehen: dann sind Übersicht und Spulen frei */
  battleDone: false,
  /** Aufstellen: angeklickter Gegner (Index in S.enemies), zeigt sein erstes Ziel */
  setupSelected: null as number | null,
  /** K27: Marschrichtung je Freund, relativ zu seinem Platz */
  marches: new Map<string, { dx: number; dy: number }>(),
  /** K27: An wessen Pfeil gerade gezogen wird */
  aiming: null as string | null,
  /** S10 Leibwache: gewählter Schützling je Freund (Schlüssel → Schlüssel) */
  wards: new Map<string, string>(),
  /** S10: An wessen Schild-Griff gerade gezogen wird, und wohin */
  wardAiming: null as string | null,
  wardAim: null as { x: number; y: number } | null,
};

// Startaufstellung: zwei Reihen in der Mitte der eigenen Hälfte. Aufstellen ist Fabis Sache.
// Fabi, Phase 3: Das Ufer ist am Anfang leer, man holt sich selbst, wen man will, vom Boot.

export function rerollEnemies(): void {
  if (S.mode === 'run') return;
  const lineup = lineups[S.lineupIdx]!;
  // K23: pro Versuch neu gewürfelt; der allererste Versuch zeigt die vorbereiteten Eigenschaften, ohne ausgeblendete (K33)
  S.enemies = S.attempt === 0 ? lineup.enemies.map((e) => ({ ...e, traits: (e.traits ?? []).filter((t) => !traitHidden(t)) })) : rollTraits(lineup.enemies, (Math.random() * 2 ** 31) | 0);
}

export type Info = {
  kind: 'friend' | 'enemy' | 'tree';
  id: string;
  traits: string[];
  unit?: Unit;
  enemyIndex?: number;
  member?: Member;
  infested?: NonNullable<EnemyPlacement['infested']>;
  items?: string[];
  /** S10 Leibwache: Name des Schützlings beim Aufstellen */
  ward?: string;
};

export const stormLevel = () => combatRules.stormLevels.find((l) => l.id === S.storm)!;

/** Gleiche Gegner durchnummerieren; Befallene heißen beim Namen (E6) */
export function enemyLabels(list: EnemyPlacement[]): string[] {
  const storm = enemyNames(list.filter((e) => e.enemy).map((e) => e.enemy!));
  let i = 0;
  return list.map((e) => (e.infested ? `${e.infested.name} (befallen)` : storm[i++]!));
}


export const view = { setupUnits: [] as DrawUnit[], setupInfo: [] as Info[] };

export const maxOnField = () => S.fieldSlots;

export const bench = () => S.roster.filter((m) => !S.placed.has(m.key)).map((m) => m.key);

/** I26: Im Run nur die Schwellen, die es dort gibt; beim Üben alle ohne Artefakt */
export function retreatIds(): string[] {
  // K43 (Fabi, Phase Kern): ohne Rückzug im Kampf wird überall bis zum Ende gekämpft
  if (!systemOn('retreat')) return ['letzter'];
  return S.run?.retreatOptions ?? combatRules.retreatThresholds.filter((r) => !r.needs).map((r) => r.id);
}

/** Bild zum Zeitpunkt t; Zeiten sind auf zwei Stellen gerundet, darum mit kleiner Zugabe */
export function frameIndex(r: BattleResult, t: number): number {
  if (t >= r.duration - 1e-6) return r.frames.length - 1;
  return Math.min(r.frames.length - 1, Math.floor(t / combatRules.tickSeconds + 1e-6));
}

export function currentFrame() {
  const r = S.result;
  if (!r || r.frames.length === 0) return null;
  const i = frameIndex(r, S.playT);
  return r.frames[i]!;
}
